/**
 * SourcesTab — News source health dashboard + Perplexity discovery settings.
 * Surfaces per-feed status (healthy/degraded/dead), last fetch, last HTTP code,
 * and articles count in the last 7 days / 24 hours. Lets admins enable/disable
 * Perplexity-powered discovery and edit topic queries.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TabsContent } from "../ui/tabs";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { RefreshCw, Check, AlertCircle, Activity, Sparkles, ExternalLink, Plus, X, PlayCircle, Eye, Trash2, Clock, Pencil, Brain, TrendingUp, ChevronDown, Flame, Newspaper, BookOpen, Zap, Upload, Power } from "lucide-react";
import ReportSourcesPanel from "./ReportSourcesPanel";
import DiscoveryStatsWidget from "./DiscoveryStatsWidget";
import YouTubeWatchlistWidget from "./YouTubeWatchlistWidget";
import { BACKEND_URL } from "../../lib/backend";
import { useAuth } from "../../AuthContext";

const API = BACKEND_URL + "/api";

const STATUS_STYLES = {
  healthy:  { bgL: "#dcfce7", bgD: "#0f3a25", fgL: "#166534", fgD: "#86efac", label: "Healthy" },
  degraded: { bgL: "#fef3c7", bgD: "#3f2e0e", fgL: "#92400e", fgD: "#fcd34d", label: "Degraded" },
  dead:     { bgL: "#fee2e2", bgD: "#3f1414", fgL: "#991b1b", fgD: "#fca5a5", label: "Dead" },
  unknown:  { bgL: "#e5e7eb", bgD: "#2a3132", fgL: "#374151", fgD: "#cbd5e1", label: "Unknown" },
};

const TIER_LABELS = {
  editorial:  "Editorial",
  aggregator: "Aggregator",
  community:  "Community",
  discovery:  "Discovery",
  unknown:    "—",
};

function fmtRelative(iso) {
  if (!iso) return "never";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.round(ms / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const h = Math.floor(mins / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return iso; }
}

export default function SourcesTab({
  dm, sources = [], loadingSources, loadSources,
  ppEnabled, setPpEnabled, ppTopics, setPpTopics,
  ppLastRunAt, ppLastRunCount, savingPp, savePpDiscovery,
  topicWatchers = [], savingWatcher = {},
  updateWatcher, runWatcherNow, deleteWatcher, createWatcher,
  customSources = [], verifyCustomSource, verifyCustomSourceHtml,
  createCustomSource, deleteCustomSource,
  updateCustomSource,
  prefillQuickAddUrl = "", clearPrefill,
  semanticDedup = { enabled: false, last_run_at: null, last_removed: 0 },
  savingSemanticDedup = false, saveSemanticDedup,
}) {
  const [newTopic, setNewTopic] = useState("");
  const [subTab, setSubTab] = useState("news"); // "news" | "reports"
  const [newWName, setNewWName] = useState("");
  const [newWQuery, setNewWQuery] = useState("");
  const [newWCadence, setNewWCadence] = useState(168);

  // Add-source wizard state
  const [wsMethod, setWsMethod] = useState("rss"); // "rss" | "html_scrape"
  const [wsRssUrl, setWsRssUrl] = useState("");
  const [wsIndexUrl, setWsIndexUrl] = useState("");
  const [wsIncludePattern, setWsIncludePattern] = useState("");
  const [wsName, setWsName] = useState("");
  const [wsCategory, setWsCategory] = useState("Custom");
  const [wsKeywords, setWsKeywords] = useState("");
  const [wsTier, setWsTier] = useState("editorial");
  const [wsMatchMode, setWsMatchMode] = useState("trust");
  const [wsLogoUrl, setWsLogoUrl] = useState("");
  const [wsIsItalian, setWsIsItalian] = useState(false);
  const [wsVerifying, setWsVerifying] = useState(false);
  const [wsVerifyResult, setWsVerifyResult] = useState(null);
  const [wsSaving, setWsSaving] = useState(false);

  // Quick-add by URL only
  const [qaUrl, setQaUrl] = useState("");
  const [qaSubmitting, setQaSubmitting] = useState(false);

  // Prefill handoff from the Suggestions tab ("Add as source", iter86b):
  // fill the quick-add input, jump to the news sub-tab and scroll the card
  // into view so the admin only has to hit Add.
  useEffect(() => {
    if (!prefillQuickAddUrl) return;
    setQaUrl(prefillQuickAddUrl);
    setSubTab("news");
    clearPrefill && clearPrefill();
    setTimeout(() => {
      document.querySelector("[data-testid='quick-add-source-card']")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  }, [prefillQuickAddUrl, clearPrefill]);

  // CSV bulk import modal
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [csvSubmitting, setCsvSubmitting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  // Per-source toggle pending state
  const [togglingNames, setTogglingNames] = useState(() => new Set());

  // Pull authHeader from context (not passed as prop) for the auto-detect call.
  const { authHeader } = useAuth();

  // Edit-source state (key = source name being edited)
  const [editingSource, setEditingSource] = useState(null); // {name, category, keywords, tier, match_mode}

  // Per-source performance expand state (Set of source names currently expanded)
  const [expandedPerf, setExpandedPerf] = useState(() => new Set());
  const togglePerf = (name) => {
    setExpandedPerf((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const handleVerify = async () => {
    setWsVerifyResult(null);
    if (wsMethod === "rss") {
      if (!wsRssUrl.trim()) return;
      setWsVerifying(true);
      try {
        const r = await verifyCustomSource(wsRssUrl.trim());
        setWsVerifyResult(r);
        if (r?.ok && r?.feed_title && !wsName) setWsName(r.feed_title.slice(0, 60));
      } finally { setWsVerifying(false); }
    } else {
      if (!wsIndexUrl.trim()) return;
      setWsVerifying(true);
      try {
        const r = await verifyCustomSourceHtml(wsIndexUrl.trim(), wsIncludePattern.trim());
        setWsVerifyResult(r);
      } finally { setWsVerifying(false); }
    }
  };

  const handleSaveSource = async () => {
    setWsSaving(true);
    try {
      const common = {
        category: wsCategory, keywords: wsKeywords,
        tier: wsTier, match_mode: wsMatchMode,
        logo_url: wsLogoUrl.trim() || null,
        is_italian: wsIsItalian,
      };
      const payload = wsMethod === "rss"
        ? { name: wsName, method: "rss", rss_url: wsRssUrl, ...common }
        : { name: wsName, method: "html_scrape", index_url: wsIndexUrl, include_pattern: wsIncludePattern, ...common };
      const ok = await createCustomSource(payload);
      if (ok) {
        setWsRssUrl(""); setWsIndexUrl(""); setWsIncludePattern("");
        setWsName(""); setWsKeywords(""); setWsCategory("Custom");
        setWsTier("editorial"); setWsMatchMode("trust"); setWsVerifyResult(null);
        setWsLogoUrl(""); setWsIsItalian(false);
      }
    } finally { setWsSaving(false); }
  };

  // Quick-add by URL: autodetect RSS feed, name, logo, language → commit
  // immediately. On failure (no RSS found), prefill the manual wizard so the
  // admin can still proceed without re-typing the URL.
  const handleQuickAdd = async () => {
    const url = qaUrl.trim();
    if (!url) return;
    if (!url.startsWith("http")) { toast.error("Enter a valid URL starting with http(s)"); return; }
    setQaSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/custom-sources/auto-detect`,
        { url, commit: true },
        { headers: authHeader() }
      );
      if (data.success && data.committed) {
        const src = data.source || {};
        toast.success(`Added "${src.name}" via auto-detect (${src.is_italian ? "🇮🇹 Italian" : "🇬🇧 English"})`);
        setQaUrl("");
        if (loadSources) await loadSources();
      } else if (!data.success) {
        // Prefill manual wizard from detected fields so the admin can finish manually.
        const d = data.detected || {};
        setWsMethod(d.rss_url ? "rss" : "html_scrape");
        if (d.rss_url) setWsRssUrl(d.rss_url);
        else setWsIndexUrl(url);
        setWsName(d.name || "");
        setWsLogoUrl(d.logo_url || "");
        setWsIsItalian(!!d.is_italian);
        toast.error(data.error || "Auto-detect failed — please finish the form below");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Auto-detect failed";
      toast.error(msg);
    } finally {
      setQaSubmitting(false);
    }
  };

  // Toggle a single source on/off — works for both static (NEWS_SOURCES) and
  // admin-managed custom sources. The backend routes the update to the right
  // collection automatically.
  const handleToggleSource = async (sourceName, nextEnabled) => {
    setTogglingNames((prev) => new Set(prev).add(sourceName));
    try {
      await axios.patch(
        `${API}/admin/sources/${encodeURIComponent(sourceName)}/toggle`,
        { enabled: nextEnabled },
        { headers: authHeader() }
      );
      toast.success(`${sourceName} ${nextEnabled ? "enabled" : "disabled"}`);
      if (loadSources) await loadSources();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Toggle failed";
      toast.error(msg);
    } finally {
      setTogglingNames((prev) => {
        const next = new Set(prev);
        next.delete(sourceName);
        return next;
      });
    }
  };

  // CSV bulk import — upserts custom sources by `name`. Existing rows are
  // updated in place (no duplicates), new rows are inserted.
  const handleCsvImport = async () => {
    if (!csvText.trim()) return;
    setCsvSubmitting(true);
    setCsvResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/custom-sources/bulk-csv`,
        { csv_text: csvText },
        { headers: authHeader() }
      );
      setCsvResult(data);
      if (data.added + data.updated > 0) {
        toast.success(`Imported: ${data.added} added, ${data.updated} updated, ${data.skipped} skipped`);
        if (loadSources) await loadSources();
      } else if (data.skipped > 0) {
        toast.error(`No sources imported — ${data.skipped} rows had errors`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "CSV import failed";
      toast.error(msg);
      setCsvResult({ success: false, error: msg });
    } finally {
      setCsvSubmitting(false);
    }
  };

  const handleCsvFile = (file) => {
    if (!file) return;
    if (file.size > 500_000) {
      toast.error("CSV file too large (max 500KB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setCsvText(e.target?.result?.toString() || "");
    reader.onerror = () => toast.error("Failed to read CSV file");
    reader.readAsText(file);
  };

  const counts = sources.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <TabsContent value="sources" className="space-y-6 mt-6" data-testid="sources-tab-content">
      <div>
        <h2 className={`text-2xl ${dm ? "text-slate-100" : "text-[#191c1d]"}`} style={{ fontFamily: "'Reckless Neue Variable', serif", fontWeight: 500, letterSpacing: "-0.01em" }}>
          Discovery & Sources
        </h2>
        <p className={`category-tag mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
          Per-source health monitor + Perplexity news discovery configuration.
        </p>
      </div>

      {/* Sub-tab toggle: News Sources | Report & Research Sources */}
      <div className="flex gap-2" data-testid="sources-subtab-toggle">
        <button
          onClick={() => setSubTab("news")}
          data-testid="subtab-news-sources"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-colors ${
            subTab === "news"
              ? (dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white")
              : (dm ? "bg-[#1e2627] text-slate-400 hover:bg-[#252e2f]" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e8ecee]")
          }`}
        >
          <Newspaper className="w-3.5 h-3.5" />
          News Sources
        </button>
        <button
          onClick={() => setSubTab("reports")}
          data-testid="subtab-report-sources"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-colors ${
            subTab === "reports"
              ? (dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white")
              : (dm ? "bg-[#1e2627] text-slate-400 hover:bg-[#252e2f]" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e8ecee]")
          }`}
        >
          <BookOpen className="w-3.5 h-3.5" />
          Report &amp; Research Sources
        </button>
      </div>

      {subTab === "reports" && <ReportSourcesPanel dm={dm} />}

      {subTab === "news" && (<>
      {/* Discovery curators health widget */}
      <DiscoveryStatsWidget dm={dm} />

      {/* YouTube watchlist — 12h auto-ingestion of channel videos */}
      <YouTubeWatchlistWidget dm={dm} />

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {["healthy", "degraded", "dead", "unknown"].map((s) => {
          const st = STATUS_STYLES[s];
          return (
            <div key={s} className={`rounded-2xl p-4 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
              <p className={`category-tag mb-2 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{st.label}</p>
              <p className="text-3xl font-semibold" style={{ color: dm ? st.fgD : st.fgL, fontFamily: "'Reckless Neue Variable', serif" }}>
                {counts[s] || 0}
              </p>
            </div>
          );
        })}
      </div>

      {/* Source list */}
      <div className={`rounded-2xl overflow-hidden ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
        <div className={`px-5 py-3 border-b flex items-center justify-between ${dm ? "border-white/5" : "border-black/4"}`}>
          <div className="flex items-center gap-2">
            <Activity className={`w-4 h-4 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`} />
            <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>News Sources</p>
            <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{sources.length} configured</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setCsvOpen(true); setCsvResult(null); }}
              data-testid="open-csv-import-btn"
              title="Bulk import / update custom sources from CSV"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md category-tag transition-colors ${dm ? "bg-amber-400/10 text-amber-300 hover:bg-amber-400/20" : "bg-amber-500/10 text-amber-700 hover:bg-amber-500/20"}`}
            >
              <Upload className="w-3.5 h-3.5" />
              Import CSV
            </button>
            <button
              onClick={loadSources}
              disabled={loadingSources}
              data-testid="reload-sources-btn"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md category-tag transition-colors disabled:opacity-50 ${dm ? "bg-white/5 text-slate-400 hover:bg-white/10" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e8ecee]"}`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingSources ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>
        {sources.length === 0 ? (
          <div className={`p-6 text-center category-tag ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
            No source data yet — run a scrape cycle (Actions → &quot;Refresh News&quot;) and reload.
          </div>
        ) : sources.map((s, idx) => {
          const st = STATUS_STYLES[s.status] || STATUS_STYLES.unknown;
          const hasPerf = (s.total_signal_score > 0) || (s.total_clicks > 0) || (s.trending_count > 0);
          const isExpanded = expandedPerf.has(s.name);
          return (
            <div key={s.name} data-testid={`source-row-${idx}`} className={`border-b last:border-b-0 ${dm ? "border-white/4" : "border-black/3"} ${s.enabled === false ? "opacity-50" : ""}`}>
              <div className="px-5 py-3 grid grid-cols-12 gap-3 items-center">
                {/* Status badge + name */}
                <div className="col-span-12 md:col-span-4 flex items-center gap-2.5 min-w-0">
                  <span
                    className="text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded flex-shrink-0"
                    style={{
                      background: dm ? st.bgD : st.bgL,
                      color: dm ? st.fgD : st.fgL,
                    }}
                    data-testid={`status-badge-${idx}`}
                  >
                    {st.label}
                  </span>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                      {s.name}
                      {s.quality_flag === "low" && (
                        <span
                          data-testid={`quality-low-${idx}`}
                          title="Low signal (avg <0.15 over 7d). Auto-throttled to 3 articles/cycle — consider disabling."
                          className="ml-2 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{
                            background: dm ? "rgba(248,113,113,0.16)" : "rgba(220,38,38,0.10)",
                            color: dm ? "#fca5a5" : "#b91c1c",
                          }}
                        >
                          ▼ LOW
                        </span>
                      )}
                      {s.quality_flag === "high" && (
                        <span
                          data-testid={`quality-high-${idx}`}
                          title="High signal (avg ≥0.5 over 7d). Users engage strongly with this source."
                          className="ml-2 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{
                            background: dm ? "rgba(52,211,153,0.16)" : "rgba(16,185,129,0.10)",
                            color: dm ? "#6ee7b7" : "#047857",
                          }}
                        >
                          ▲ HIGH
                        </span>
                      )}
                    </p>
                    <p className={`text-[11px] tracking-wider uppercase ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                      {TIER_LABELS[s.tier] || s.tier} · {s.category}
                    </p>
                  </div>
                </div>
                {/* Metrics: articles + performance chips */}
                <div className="col-span-6 md:col-span-3 flex flex-col">
                  <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>articles 7d / 24h</p>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <p className={`text-sm font-mono ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                      {s.articles_last_7d} <span className={dm ? "text-slate-600" : "text-[#bac9cc]"}>/</span> {s.articles_last_24h}
                    </p>
                    {s.total_signal_score > 0 && (
                      <span
                        data-testid={`perf-signal-${idx}`}
                        title={`Total signal: ${s.total_signal_score} · avg ${s.avg_signal_score}`}
                        className={`flex items-center gap-0.5 category-tag text-[10px] px-1.5 py-0.5 rounded-full ${
                          dm ? "bg-cyan-400/10 text-cyan-300" : "bg-[#006874]/8 text-[#006874]"
                        }`}
                      >
                        <TrendingUp className="w-2.5 h-2.5" />
                        {s.total_signal_score}
                      </span>
                    )}
                    {s.trending_count > 0 && (
                      <span
                        data-testid={`perf-trending-${idx}`}
                        title={`${s.trending_count} trending stor${s.trending_count === 1 ? "y" : "ies"} (early signal)`}
                        className="flex items-center gap-0.5 category-tag text-[10px] px-1.5 py-0.5 rounded-full"
                        style={{ background: dm ? "rgba(249,115,22,0.18)" : "rgba(249,115,22,0.12)", color: "#ea580c" }}
                      >
                        <Flame className="w-2.5 h-2.5" />
                        {s.trending_count}
                      </span>
                    )}
                  </div>
                </div>
                {/* Last fetch */}
                <div className="col-span-6 md:col-span-3 flex flex-col">
                  <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                    last fetch {s.last_http_status ? `· HTTP ${s.last_http_status}` : ""}
                  </p>
                  <p className={`text-xs mt-0.5 ${dm ? "text-slate-300" : "text-[#3b494c]"}`}>
                    {fmtRelative(s.last_fetch_at)}
                  </p>
                  {s.recovery_method && s.recovery_method !== "direct" && (
                    <span
                      data-testid={`recovery-badge-${idx}`}
                      title={s.recovery_method === "tls-bypass"
                        ? "Fetched via Chrome TLS-fingerprint bypass (curl_cffi) — the site's WAF blocks standard fetches"
                        : "Fetched with the honest feed-reader User-Agent — the site's WAF blocks browser UAs from datacenter IPs"}
                      className="self-start mt-1 category-tag text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full"
                      style={s.recovery_method === "tls-bypass"
                        ? { background: dm ? "rgba(168,85,247,0.18)" : "rgba(168,85,247,0.12)", color: dm ? "#c4b5fd" : "#7c3aed" }
                        : { background: dm ? "rgba(234,179,8,0.15)" : "rgba(234,179,8,0.12)", color: dm ? "#fde047" : "#a16207" }}
                    >
                      {s.recovery_method === "tls-bypass" ? "TLS BYPASS" : "FEED UA"}
                    </span>
                  )}
                  {s.last_error && (
                    <p className={`text-[10px] mt-0.5 truncate ${dm ? "text-red-400" : "text-red-600"}`} title={s.last_error}>
                      {s.last_error}
                    </p>
                  )}
                  {s.status === "degraded" && s.consecutive_failures > 0 && (
                    <p
                      data-testid={`grace-window-${idx}`}
                      className={`text-[10px] mt-0.5 ${dm ? "text-amber-400" : "text-amber-700"}`}
                      title="Will be marked dead after 3 consecutive failures over a 12h window"
                    >
                      Error {s.consecutive_failures}/3 · under observation
                      {s.first_failed_at ? ` da ${fmtRelative(s.first_failed_at)}` : ""}
                    </p>
                  )}
                </div>
                {/* Actions: Perf expand + Open feed */}
                <div className="col-span-12 md:col-span-2 flex items-center justify-end gap-2">
                  {hasPerf && (
                    <button
                      onClick={() => togglePerf(s.name)}
                      data-testid={`perf-toggle-${idx}`}
                      title="Show top 3 articles by signal"
                      className={`flex items-center gap-0.5 category-tag px-2 py-1 rounded-md transition-colors ${
                        isExpanded
                          ? (dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/12 text-[#006874]")
                          : (dm ? "text-slate-400 hover:text-cyan-300 hover:bg-white/5" : "text-[#6b7a7d] hover:text-[#006874] hover:bg-[#f3f4f5]")
                      }`}
                    >
                      Perf
                      <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </button>
                  )}
                  {s.rss_url && (
                    <a
                      href={s.rss_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`open-feed-${idx}`}
                      className={`flex items-center gap-1 category-tag transition-colors ${dm ? "text-slate-400 hover:text-cyan-300" : "text-[#6b7a7d] hover:text-[#006874]"}`}
                    >
                      Feed <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {/* Enable/disable toggle — visible for ALL sources (static + custom) */}
                  <button
                    onClick={() => handleToggleSource(s.name, !s.enabled)}
                    disabled={togglingNames.has(s.name)}
                    data-testid={`toggle-source-${idx}`}
                    title={s.enabled ? "Disable this source (skip on next refresh)" : "Re-enable this source"}
                    className={`flex items-center gap-1 category-tag px-2 py-1 rounded-md transition-colors disabled:opacity-50 ${
                      s.enabled === false
                        ? (dm ? "bg-red-500/15 text-red-300 hover:bg-red-500/25" : "bg-red-50 text-red-700 hover:bg-red-100")
                        : (dm ? "text-slate-400 hover:text-emerald-300 hover:bg-white/5" : "text-[#6b7a7d] hover:text-emerald-700 hover:bg-[#f3f4f5]")
                    }`}
                  >
                    {togglingNames.has(s.name)
                      ? <RefreshCw className="w-3 h-3 animate-spin" />
                      : <Power className="w-3 h-3" />}
                    {s.enabled === false ? "OFF" : "ON"}
                  </button>
                </div>
              </div>

              {/* Expandable performance panel: top 3 articles by signal */}
              {isExpanded && (s.top_articles?.length || 0) > 0 && (
                <div
                  data-testid={`perf-panel-${idx}`}
                  className={`px-5 pb-3 pt-1 ${dm ? "bg-white/3" : "bg-[#fafbfc]"}`}
                >
                  <p className={`category-tag text-[10px] mb-1.5 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                    Top {s.top_articles.length} by signal · 7d · avg signal {s.avg_signal_score} · {s.total_clicks} clicks
                  </p>
                  <div className="space-y-1">
                    {s.top_articles.map((a, i) => (
                      <a
                        key={i}
                        href={a.originalUrl || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`perf-article-${idx}-${i}`}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors text-xs group ${
                          dm ? "hover:bg-white/5" : "hover:bg-white"
                        }`}
                      >
                        <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          i === 0 ? "bg-orange-500/15 text-orange-500" :
                          i === 1 ? "bg-amber-400/15 text-amber-500" :
                                    "bg-yellow-400/15 text-yellow-600"
                        }`}>{i + 1}</span>
                        <span className={`truncate flex-1 ${dm ? "text-slate-300 group-hover:text-cyan-300" : "text-[#3b494c] group-hover:text-[#006874]"}`}>
                          {a.title}
                        </span>
                        <span className={`flex items-center gap-0.5 category-tag text-[10px] flex-shrink-0 ${dm ? "text-cyan-300" : "text-[#006874]"}`}
                              title={`signal ${a.signal_score} · ${a.public_clicks} clicks`}>
                          <TrendingUp className="w-2.5 h-2.5" />
                          {a.signal_score}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Perplexity discovery card */}
      <div className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`mt-0.5 w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center ${dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]"}`}>
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1.5">
                <p className={`text-sm font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
                  Perplexity AI Discovery
                </p>
                <Switch
                  checked={ppEnabled}
                  onCheckedChange={setPpEnabled}
                  data-testid="pp-enabled-toggle"
                  className="data-[state=checked]:bg-[#006874]"
                />
              </div>
              <p className={`text-xs leading-relaxed ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                Augment RSS with real-time AI web search. Runs after every scrape
                cycle; pulls top stories from press releases, trade publications
                behind Cloudflare, and conference announcements that standard RSS misses.
              </p>
              <p className={`category-tag mt-2 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
                Last run: {fmtRelative(ppLastRunAt)} · {ppLastRunCount} articles
              </p>
            </div>
          </div>
          <button
            onClick={savePpDiscovery}
            disabled={savingPp}
            data-testid="save-pp-discovery-btn"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-all disabled:opacity-50 flex-shrink-0 ${dm ? "bg-[#252e2f] text-slate-200 hover:bg-[#303b3c]" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
          >
            {savingPp ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Saving…</> : <><Check className="w-3.5 h-3.5" />Save</>}
          </button>
        </div>

        {/* Topics editor */}
        <div className={`mt-4 pt-4 border-t ${dm ? "border-white/5" : "border-black/5"}`}>
          <p className={`category-tag mb-3 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Topic queries ({ppTopics.length}/10)
          </p>
          <div className="space-y-2 mb-3">
            {ppTopics.map((t, i) => (
              <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${dm ? "bg-white/5" : "bg-[#f3f4f5]"}`}>
                <span className={`text-sm flex-1 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{t}</span>
                <button
                  onClick={() => setPpTopics(ppTopics.filter((_, idx) => idx !== i))}
                  data-testid={`remove-pp-topic-${i}`}
                  aria-label="Remove topic"
                  className={`p-1 rounded transition-colors ${dm ? "text-slate-500 hover:text-red-400" : "text-[#6b7a7d] hover:text-red-600"}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          {ppTopics.length < 10 && (
            <div className="flex gap-2">
              <Input
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newTopic.trim()) {
                    setPpTopics([...ppTopics, newTopic.trim()]);
                    setNewTopic("");
                  }
                }}
                placeholder="e.g. AI agents for hotel revenue managers"
                data-testid="new-pp-topic-input"
                className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`}
              />
              <button
                onClick={() => {
                  if (newTopic.trim()) {
                    setPpTopics([...ppTopics, newTopic.trim()]);
                    setNewTopic("");
                  }
                }}
                data-testid="add-pp-topic-btn"
                className={`px-3 rounded-md category-tag transition-colors ${dm ? "bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Diagnostic note */}
        <div className={`mt-4 pt-4 border-t flex items-start gap-2 ${dm ? "border-white/5" : "border-black/5"}`}>
          <AlertCircle className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`} />
          <p className={`text-[11px] leading-relaxed ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Requires <code className={`px-1 py-0.5 rounded ${dm ? "bg-white/5" : "bg-[#f3f4f5]"}`}>PERPLEXITY_API_KEY</code> in
            the Keys tab. Cost: ~$0.005 per topic per run. Auto-disabled if key is missing.
          </p>
        </div>
      </div>

      {/* Topic Watchers card */}
      <div className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }} data-testid="topic-watchers-card">
        <div className="flex items-start gap-3 mb-4">
          <div className={`mt-0.5 w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center ${dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]"}`}>
            <Eye className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <p className={`text-sm font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Topic Watchers</p>
            <p className={`text-xs leading-relaxed mt-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
              Scheduled, narrow Perplexity queries for specific topics (M&amp;A, AI product launches,
              DMO case studies, airline disruption). Each watcher runs on its own cadence.
            </p>
          </div>
        </div>

        {topicWatchers.length === 0 ? (
          <div className={`p-4 text-center category-tag rounded-lg ${dm ? "bg-white/3 text-slate-500" : "bg-[#f8f9fa] text-[#bac9cc]"}`}>
            No watchers configured. The 4 defaults will be seeded automatically.
          </div>
        ) : (
          <div className="space-y-2">
            {topicWatchers.map((w) => (
              <div key={w.id} data-testid={`watcher-row-${w.id}`} className={`rounded-xl p-3 ${dm ? "bg-white/3" : "bg-[#f8f9fa]"}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Switch
                      checked={!!w.enabled}
                      onCheckedChange={(v) => updateWatcher(w.id, { enabled: v })}
                      data-testid={`watcher-enable-${w.id}`}
                      className="data-[state=checked]:bg-[#006874]"
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{w.name}</p>
                      <p className={`text-[11px] truncate ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{w.query}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`category-tag flex items-center gap-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                      <Clock className="w-3 h-3" />
                      every {w.cadence_hours === 168 ? "7d" : w.cadence_hours === 720 ? "30d" : `${w.cadence_hours}h`}
                    </span>
                    <span className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                      {fmtRelative(w.last_run_at)} · {w.last_results_count}
                    </span>
                    <button
                      onClick={() => runWatcherNow(w.id)}
                      disabled={!!savingWatcher[w.id]}
                      data-testid={`watcher-run-${w.id}`}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md category-tag transition-colors disabled:opacity-50 ${dm ? "bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
                    >
                      {savingWatcher[w.id] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
                      Run
                    </button>
                    <button
                      onClick={() => deleteWatcher(w.id)}
                      data-testid={`watcher-delete-${w.id}`}
                      aria-label="Delete watcher"
                      className={`p-1.5 rounded-md transition-colors ${dm ? "text-slate-500 hover:text-red-400" : "text-[#6b7a7d] hover:text-red-600"}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {w.last_error && (
                  <p className={`text-[10px] mt-2 px-2 py-1 rounded ${dm ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-700"}`}>
                    Last error: {w.last_error}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add new watcher */}
        <div className={`mt-4 pt-4 border-t ${dm ? "border-white/5" : "border-black/5"}`}>
          <p className={`category-tag mb-2 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Add new watcher</p>
          <div className="grid gap-2">
            <Input
              value={newWName} onChange={(e) => setNewWName(e.target.value)}
              placeholder="Name (e.g. AI in cruise lines)"
              data-testid="new-watcher-name"
              className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`}
            />
            <Input
              value={newWQuery} onChange={(e) => setNewWQuery(e.target.value)}
              placeholder="Perplexity query (e.g. AI adoption by cruise lines in 2026)"
              data-testid="new-watcher-query"
              className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`}
            />
            <div className="flex items-center gap-2">
              <select
                value={newWCadence}
                onChange={(e) => setNewWCadence(Number(e.target.value))}
                data-testid="new-watcher-cadence"
                className={`flex-1 text-sm rounded-md px-3 py-2 border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5] text-[#191c1d]"}`}
              >
                <option value={24}>Daily</option>
                <option value={168}>Weekly</option>
                <option value={720}>Monthly</option>
              </select>
              <button
                onClick={async () => {
                  if (!newWName.trim() || !newWQuery.trim()) return;
                  const ok = await createWatcher({
                    name: newWName, query: newWQuery,
                    cadence_hours: newWCadence, lookback_hours: newWCadence,
                  });
                  if (ok) { setNewWName(""); setNewWQuery(""); }
                }}
                data-testid="create-watcher-btn"
                className={`flex items-center gap-1 px-4 py-2 rounded-md category-tag transition-colors ${dm ? "bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Add by URL — autodetect RSS/name/logo/lang and commit in one click */}
      <div
        className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`}
        style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}
        data-testid="quick-add-source-card"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className={`mt-0.5 w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center ${dm ? "bg-amber-400/10 text-amber-300" : "bg-amber-500/10 text-amber-600"}`}>
            <Zap className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <p className={`text-sm font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Quick add by URL</p>
            <p className={`text-xs leading-relaxed mt-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
              Paste a homepage URL. We auto-detect the <strong>RSS feed</strong>, site name, logo and language, then add it instantly. If RSS isn&apos;t found, the form below is pre-filled so you can finish manually.
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Input
            value={qaUrl}
            onChange={(e) => setQaUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !qaSubmitting) handleQuickAdd(); }}
            placeholder="https://www.example.com"
            data-testid="quick-add-url-input"
            disabled={qaSubmitting}
            className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`}
          />
          <button
            onClick={handleQuickAdd}
            disabled={!qaUrl.trim() || qaSubmitting}
            data-testid="quick-add-submit-btn"
            className={`flex items-center gap-1 px-4 py-2 rounded-md category-tag transition-colors disabled:opacity-50 flex-shrink-0 ${
              dm ? "bg-amber-400/15 text-amber-300 hover:bg-amber-400/25" : "bg-amber-500/10 text-amber-700 hover:bg-amber-500/20"
            }`}
          >
            {qaSubmitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            {qaSubmitting ? "Detecting…" : "Auto-detect & add"}
          </button>
        </div>
      </div>

      {/* Add custom source wizard */}
      <div className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }} data-testid="add-source-wizard">
        <div className="flex items-start gap-3 mb-4">
          <div className={`mt-0.5 w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center ${dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]"}`}>
            <Plus className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <p className={`text-sm font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Add custom source</p>
            <p className={`text-xs leading-relaxed mt-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
              Two modes: <strong>RSS</strong> (paste a feed URL) or <strong>HTML scrape</strong> (paste an index page, Firecrawl maps the article links — supports Cloudflare-protected sites without RSS).
            </p>
          </div>
        </div>

        {/* Method toggle */}
        <div className="flex gap-2 mb-3" data-testid="ws-method-toggle">
          <button
            onClick={() => { setWsMethod("rss"); setWsVerifyResult(null); }}
            data-testid="ws-method-rss"
            className={`flex-1 px-3 py-2 rounded-md category-tag transition-colors ${
              wsMethod === "rss"
                ? (dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]")
                : (dm ? "bg-white/3 text-slate-400 hover:bg-white/5" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e8ecee]")
            }`}
          >
            RSS feed
          </button>
          <button
            onClick={() => { setWsMethod("html_scrape"); setWsVerifyResult(null); }}
            data-testid="ws-method-html"
            className={`flex-1 px-3 py-2 rounded-md category-tag transition-colors ${
              wsMethod === "html_scrape"
                ? (dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]")
                : (dm ? "bg-white/3 text-slate-400 hover:bg-white/5" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e8ecee]")
            }`}
          >
            HTML scrape <span className="opacity-60">(Firecrawl)</span>
          </button>
        </div>

        {/* Step 1: URL + verify */}
        <div className="space-y-2">
          {wsMethod === "rss" ? (
            <div className="flex gap-2">
              <Input
                value={wsRssUrl}
                onChange={(e) => { setWsRssUrl(e.target.value); setWsVerifyResult(null); }}
                placeholder="https://example.com/feed.xml"
                data-testid="ws-rss-url"
                className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`}
              />
              <button
                onClick={handleVerify}
                disabled={!wsRssUrl.trim() || wsVerifying}
                data-testid="ws-verify-btn"
                className={`flex items-center gap-1 px-4 py-2 rounded-md category-tag transition-colors disabled:opacity-50 flex-shrink-0 ${dm ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e8ecee]"}`}
              >
                {wsVerifying ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                Verify
              </button>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <Input
                  value={wsIndexUrl}
                  onChange={(e) => { setWsIndexUrl(e.target.value); setWsVerifyResult(null); }}
                  placeholder="https://www.example.com/  (or /blog/, /news/)"
                  data-testid="ws-index-url"
                  className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`}
                />
                <button
                  onClick={handleVerify}
                  disabled={!wsIndexUrl.trim() || wsVerifying}
                  data-testid="ws-verify-btn"
                  className={`flex items-center gap-1 px-4 py-2 rounded-md category-tag transition-colors disabled:opacity-50 flex-shrink-0 ${dm ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e8ecee]"}`}
                >
                  {wsVerifying ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                  Map
                </button>
              </div>
              <Input
                value={wsIncludePattern}
                onChange={(e) => { setWsIncludePattern(e.target.value); setWsVerifyResult(null); }}
                placeholder="Optional URL filter (e.g. /news/ or /blog/)"
                data-testid="ws-include-pattern"
                className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`}
              />
            </>
          )}

          {/* Verify result */}
          {wsVerifyResult && (
            <div
              className={`text-xs px-3 py-2 rounded-md ${wsVerifyResult.ok
                ? (dm ? "bg-emerald-500/10 text-emerald-300" : "bg-emerald-50 text-emerald-800")
                : (dm ? "bg-red-500/10 text-red-300" : "bg-red-50 text-red-800")}`}
              data-testid="ws-verify-result"
            >
              {wsVerifyResult.ok ? (
                wsMethod === "rss" ? (
                  <>✓ HTTP {wsVerifyResult.http_status} · {wsVerifyResult.entries} entries · sample: <em>&quot;{wsVerifyResult.sample?.slice(0, 80)}…&quot;</em></>
                ) : (
                  <>
                    ✓ Mapped <strong>{wsVerifyResult.total}</strong> links · preview:
                    <ul className="mt-1.5 space-y-0.5 max-h-32 overflow-y-auto">
                      {(wsVerifyResult.preview || []).map((u, i) => (
                        <li key={i} className="truncate font-mono text-[10px] opacity-80">↪ {u}</li>
                      ))}
                    </ul>
                  </>
                )
              ) : (
                <>✗ {wsVerifyResult.error || "Failed"}</>
              )}
            </div>
          )}
        </div>

        {/* Step 2: metadata (only if verify ok) */}
        {wsVerifyResult?.ok && (
          <div className={`mt-4 pt-4 border-t space-y-2 ${dm ? "border-white/5" : "border-black/5"}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Input value={wsName} onChange={(e) => setWsName(e.target.value)} placeholder="Source name" data-testid="ws-name"
                className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
              <Input value={wsCategory} onChange={(e) => setWsCategory(e.target.value)} placeholder="Category (e.g. Hotel Tech)" data-testid="ws-category"
                className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
            </div>
            <Input value={wsKeywords} onChange={(e) => setWsKeywords(e.target.value)} placeholder="Keywords (comma-separated, e.g. hotel, hospitality, lodging)" data-testid="ws-keywords"
              className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-center">
              <Input value={wsLogoUrl} onChange={(e) => setWsLogoUrl(e.target.value)}
                placeholder="Logo URL (optional, e.g. /source_logos/brand.png or https://…)"
                data-testid="ws-logo-url"
                className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
              <label className={`flex items-center gap-2 cursor-pointer category-tag px-3 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                <input type="checkbox" checked={wsIsItalian} onChange={(e) => setWsIsItalian(e.target.checked)}
                  data-testid="ws-is-italian"
                  className="w-3.5 h-3.5 accent-cyan-400 rounded" />
                Italian source 🇮🇹
              </label>
            </div>
            <div className="flex items-center gap-2">
              <select value={wsTier} onChange={(e) => setWsTier(e.target.value)} data-testid="ws-tier"
                className={`flex-1 text-sm rounded-md px-3 py-2 border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5] text-[#191c1d]"}`}>
                <option value="editorial">Editorial</option>
                <option value="aggregator">Aggregator</option>
                <option value="community">Community</option>
              </select>
              <select value={wsMatchMode} onChange={(e) => setWsMatchMode(e.target.value)} data-testid="ws-match-mode"
                className={`flex-1 text-sm rounded-md px-3 py-2 border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5] text-[#191c1d]"}`}>
                <option value="trust">Trust (any keyword)</option>
                <option value="strict">Strict (AI + travel)</option>
              </select>
              <button
                onClick={handleSaveSource}
                disabled={!wsName.trim() || !wsKeywords.trim() || wsSaving}
                data-testid="ws-save-btn"
                className={`flex items-center gap-1 px-4 py-2 rounded-md category-tag transition-colors disabled:opacity-50 ${dm ? "bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
              >
                {wsSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Save
              </button>
            </div>
          </div>
        )}

        {/* Existing custom sources */}
        {customSources.length > 0 && (
          <div className={`mt-4 pt-4 border-t ${dm ? "border-white/5" : "border-black/5"}`}>
            <p className={`category-tag mb-2 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
              Custom sources ({customSources.length})
            </p>
            <div className="space-y-1">
              {customSources.map((cs) => {
                const isEditing = editingSource?.name === cs.name;
                const isDisabled = cs.enabled === false;
                return (
                  <div key={cs.name} data-testid={`custom-source-${cs.name}`}
                    className={`rounded-md ${dm ? "bg-white/3" : "bg-[#f8f9fa]"} ${isDisabled ? "opacity-60" : ""}`}>
                    <div className="flex items-center justify-between px-3 py-2 gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Switch
                          checked={cs.enabled !== false}
                          onCheckedChange={(v) => updateCustomSource && updateCustomSource(cs.name, { enabled: v })}
                          data-testid={`custom-source-toggle-${cs.name}`}
                          className="data-[state=checked]:bg-[#006874]"
                        />
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-medium truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{cs.name}</p>
                          <p className={`text-[11px] truncate ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                            {cs.method === "html_scrape" ? "html-scrape" : "rss"} · {cs.tier} · {cs.match_mode} · {cs.category}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => setEditingSource(isEditing ? null : {
                            name: cs.name,
                            category: cs.category || "Custom",
                            keywords: (cs.keywords || []).join(", "),
                            tier: cs.tier || "editorial",
                            match_mode: cs.match_mode || "trust",
                          })}
                          data-testid={`edit-custom-source-${cs.name}`}
                          aria-label="Edit"
                          className={`p-1.5 rounded-md transition-colors ${
                            isEditing
                              ? (dm ? "bg-cyan-400/20 text-cyan-300" : "bg-[#006874]/15 text-[#006874]")
                              : (dm ? "text-slate-500 hover:text-cyan-300" : "text-[#6b7a7d] hover:text-[#006874]")
                          }`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => deleteCustomSource(cs.name)}
                          data-testid={`delete-custom-source-${cs.name}`}
                          aria-label="Delete"
                          className={`p-1.5 rounded-md transition-colors ${dm ? "text-slate-500 hover:text-red-400" : "text-[#6b7a7d] hover:text-red-600"}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {/* Inline edit panel */}
                    {isEditing && (
                      <div className={`px-3 pb-3 pt-1 space-y-2 border-t ${dm ? "border-white/5" : "border-black/5"}`} data-testid={`edit-panel-${cs.name}`}>
                        <Input
                          value={editingSource.category}
                          onChange={(e) => setEditingSource({ ...editingSource, category: e.target.value })}
                          placeholder="Category"
                          data-testid={`edit-category-${cs.name}`}
                          className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-white"}`}
                        />
                        <Input
                          value={editingSource.keywords}
                          onChange={(e) => setEditingSource({ ...editingSource, keywords: e.target.value })}
                          placeholder="Keywords (comma-separated)"
                          data-testid={`edit-keywords-${cs.name}`}
                          className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-white"}`}
                        />
                        <div className="flex items-center gap-2">
                          <select
                            value={editingSource.tier}
                            onChange={(e) => setEditingSource({ ...editingSource, tier: e.target.value })}
                            data-testid={`edit-tier-${cs.name}`}
                            className={`flex-1 text-sm rounded-md px-3 py-2 border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-white text-[#191c1d]"}`}
                          >
                            <option value="editorial">Editorial</option>
                            <option value="aggregator">Aggregator</option>
                            <option value="community">Community</option>
                          </select>
                          <select
                            value={editingSource.match_mode}
                            onChange={(e) => setEditingSource({ ...editingSource, match_mode: e.target.value })}
                            data-testid={`edit-match-mode-${cs.name}`}
                            className={`flex-1 text-sm rounded-md px-3 py-2 border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-white text-[#191c1d]"}`}
                          >
                            <option value="trust">Trust</option>
                            <option value="strict">Strict</option>
                          </select>
                          <button
                            onClick={async () => {
                              const ok = await updateCustomSource(cs.name, {
                                category: editingSource.category,
                                keywords: editingSource.keywords,
                                tier: editingSource.tier,
                                match_mode: editingSource.match_mode,
                              });
                              if (ok) setEditingSource(null);
                            }}
                            data-testid={`save-edit-${cs.name}`}
                            className={`flex items-center gap-1 px-3 py-2 rounded-md category-tag transition-colors ${dm ? "bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
                          >
                            <Check className="w-3.5 h-3.5" /> Save
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Semantic Dedup card */}
      <div className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }} data-testid="semantic-dedup-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`mt-0.5 w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center ${dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]"}`}>
              <Brain className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1.5">
                <p className={`text-sm font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
                  Semantic Deduplication (AI Embeddings)
                </p>
                <Switch
                  checked={!!semanticDedup.enabled}
                  onCheckedChange={(v) => saveSemanticDedup && saveSemanticDedup(v)}
                  disabled={savingSemanticDedup}
                  data-testid="semantic-dedup-toggle"
                  className="data-[state=checked]:bg-[#006874]"
                />
              </div>
              <p className={`text-xs leading-relaxed ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                Second-pass dedup using OpenAI <code className={`px-1 py-0.5 rounded ${dm ? "bg-white/5" : "bg-[#f3f4f5]"}`}>text-embedding-3-small</code>.
                Catches paraphrased headlines that fuzzy matching misses (e.g.
                <em> &quot;Marriott to acquire X&quot;</em> vs <em>&quot;X bought by Marriott in $2B deal&quot;</em>).
              </p>
              <p className={`category-tag mt-2 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
                Last run: {fmtRelative(semanticDedup.last_run_at)} · removed {semanticDedup.last_removed} duplicates
              </p>
            </div>
          </div>
        </div>
        <div className={`mt-4 pt-4 border-t flex items-start gap-2 ${dm ? "border-white/5" : "border-black/5"}`}>
          <AlertCircle className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`} />
          <p className={`text-[11px] leading-relaxed ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Requires <code className={`px-1 py-0.5 rounded ${dm ? "bg-white/5" : "bg-[#f3f4f5]"}`}>OPENAI_API_KEY</code> in
            the Keys tab. Cost: ~$0.00002 per article (negligible). Embeddings are persisted and reused across scrape cycles.
          </p>
        </div>
      </div>
      </>)}

      {/* ─── CSV BULK IMPORT MODAL ─────────────────────────────────────── */}
      {csvOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.55)" }}
          onClick={(e) => { if (e.target === e.currentTarget && !csvSubmitting) setCsvOpen(false); }}
          data-testid="csv-import-modal"
        >
          <div
            className={`w-full max-w-2xl rounded-2xl ${dm ? "bg-[#1a2122]" : "bg-white"}`}
            style={{ boxShadow: "0 12px 48px rgba(0,0,0,0.4)" }}
          >
            <div className={`px-6 py-4 border-b flex items-center justify-between ${dm ? "border-white/8" : "border-black/8"}`}>
              <div>
                <h3 className={`text-base font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
                  Bulk import sources from CSV
                </h3>
                <p className={`text-xs mt-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                  Existing rows (matched by <code className="px-1 rounded bg-black/10">name</code>) are <strong>updated</strong>. New rows are added.
                </p>
              </div>
              <button
                onClick={() => !csvSubmitting && setCsvOpen(false)}
                data-testid="csv-modal-close"
                className={`p-1.5 rounded-md transition-colors ${dm ? "hover:bg-white/10 text-slate-400" : "hover:bg-black/5 text-[#6b7a7d]"}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Schema reminder */}
              <div className={`text-[11px] leading-relaxed p-3 rounded-lg ${dm ? "bg-cyan-400/10 text-cyan-200" : "bg-cyan-50 text-cyan-900"}`}>
                <strong>✨ Smart mode (LLM):</strong> any CSV schema is OK — column headers can be in any language
                (Italian, English…), separator can be comma or semicolon. GPT-4o-mini maps your columns to our
                canonical fields automatically.<br />
                <span className="opacity-75">
                  Each row needs at least a <strong>name/title</strong> and an <strong>RSS feed URL</strong>.
                  Optional: category, keywords, tier (editorial/aggregator/community), match_mode (trust/strict),
                  logo URL, language.
                </span>
              </div>

              {/* Results panel: LLM badge */}
              {csvResult?.normalized_via_llm && (
                <div className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded inline-flex items-center gap-1 ${dm ? "bg-amber-400/15 text-amber-300" : "bg-amber-500/10 text-amber-700"}`}>
                  <Sparkles className="w-3 h-3" /> Normalized by GPT-4o-mini
                </div>
              )}

              {/* File picker */}
              <div className="flex items-center gap-3">
                <label className={`flex items-center gap-2 px-3 py-2 rounded-md category-tag cursor-pointer transition-colors ${dm ? "bg-amber-400/15 text-amber-300 hover:bg-amber-400/25" : "bg-amber-500/10 text-amber-700 hover:bg-amber-500/20"}`}>
                  <Upload className="w-3.5 h-3.5" />
                  Choose CSV file
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    data-testid="csv-file-input"
                    onChange={(e) => handleCsvFile(e.target.files?.[0])}
                    disabled={csvSubmitting}
                  />
                </label>
                <span className={`text-xs ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
                  or paste below ↓
                </span>
              </div>

              {/* Textarea */}
              <textarea
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder={`name,rss_url,category,keywords,tier,match_mode\nSkift,https://skift.com/feed/,Travel News,&quot;travel,tourism,hotel&quot;,editorial,trust`}
                rows={8}
                disabled={csvSubmitting}
                data-testid="csv-textarea"
                className={`w-full px-3 py-2 rounded-lg font-mono text-xs outline-none resize-y ${dm ? "bg-[#252e2f] text-slate-200 border border-white/5 placeholder:text-slate-600" : "bg-[#f3f4f5] text-[#191c1d] border border-black/5 placeholder:text-[#bac9cc]"}`}
              />

              {/* Results panel */}
              {csvResult && (
                <div
                  data-testid="csv-result"
                  className={`text-xs p-3 rounded-lg ${dm ? "bg-emerald-400/10 text-emerald-300" : "bg-emerald-50 text-emerald-800"}`}
                >
                  ✓ Imported: <strong>{csvResult.added}</strong> added · <strong>{csvResult.updated}</strong> updated · <strong>{csvResult.skipped}</strong> skipped
                  {csvResult.discovery_resolved > 0 && (
                    <div
                      data-testid="csv-discovery-resolved"
                      className={`mt-1 ${dm ? "text-cyan-300" : "text-cyan-700"}`}
                    >
                      <Sparkles className="inline w-3 h-3 mr-1" />
                      Auto-discovered real RSS for <strong>{csvResult.discovery_resolved}</strong> source(s) (sitemap URLs replaced)
                    </div>
                  )}
                  {csvResult.discovery_unresolved_total > 0 && (
                    <details className="mt-2">
                      <summary
                        data-testid="csv-discovery-unresolved-summary"
                        className={`cursor-pointer ${dm ? "text-amber-300" : "text-amber-700"}`}
                      >
                        ⚠ {csvResult.discovery_unresolved_total} source(s) kept sitemap URL — no RSS feed found
                      </summary>
                      <ul className="mt-2 space-y-1 font-mono">
                        {csvResult.discovery_unresolved.map((u, i) => (
                          <li key={i}>· {u.name}: {u.kept_url}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {csvResult.errors?.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer">View {csvResult.total_errors} error(s)</summary>
                      <ul className="mt-2 space-y-1 font-mono">
                        {csvResult.errors.map((er, i) => (
                          <li key={i}>· Row {er.row}: {er.reason}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>

            <div className={`px-6 py-4 border-t flex items-center justify-end gap-2 ${dm ? "border-white/8 bg-black/15" : "border-black/8 bg-[#fafbfc]"}`}>
              <button
                onClick={() => setCsvOpen(false)}
                disabled={csvSubmitting}
                data-testid="csv-cancel-btn"
                className={`px-3 py-1.5 rounded-md category-tag ${dm ? "text-slate-400 hover:bg-white/5" : "text-[#6b7a7d] hover:bg-black/5"}`}
              >
                Close
              </button>
              <button
                onClick={handleCsvImport}
                disabled={!csvText.trim() || csvSubmitting}
                data-testid="csv-import-submit"
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md category-tag font-semibold disabled:opacity-50 transition-colors ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
              >
                {csvSubmitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {csvSubmitting ? "Importing…" : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}
    </TabsContent>
  );
}
